<template>
  <div class="l-page l-page--message-edit">
    <main class="l-main">
      <div class="l-main-head">
        <div class="c-title u-w600">
          <div class="c-title__text c-title__text--blue u-fs34">
            {{ messageInfo.messageId ? 'ユニークキー配信の編集' : 'ユニークキー配信の作成' }}
          </div>
        </div>
        <div class="c-progress">
          <ul class="c-progress-list">
            <li class="c-progress-list__item is-current">配信内容の作成</li>
            <li class="c-progress-list__item">配信内容の確認</li>
            <li class="c-progress-list__item">設定完了</li>
          </ul>
        </div>
      </div>

      <div class="l-main-content">
        <div class="p-message-edit">
          <div class="p-message-edit-list p-message-edit-list--first">
            <div class="p-message-edit-list__item">
              <div class="c-select u-w50p" :class="{ 'is-error': listError }">
                <div class="c-input__title">
                  <span class="c-input__text c-input__text--required">【必須】</span>配信先のユニークキーリスト
                </div>
                <select
                  id="uniqueKeyMessageList"
                  v-model="listId"
                  class="unique-key-message-list"
                  name="list_id"
                >
                  <option :value="null" disabled>リストを選んでください</option>
                  <option v-for="list in lists" :key="list.list_id" :value="list.list_id">
                    {{ list.list_name }}（{{ list.key_count.toLocaleString() }} キー）
                  </option>
                </select>
                <p v-if="listError" class="c-error__text">{{ listError }}</p>
              </div>
              <p v-if="listId" class="unique-key-message-reach">
                配信対象数：
                <span v-if="reach" class="unique-key-message-reach__value">{{ reach.reach_count.toLocaleString() }} 件</span>
                <span v-else>{{ reachFailed ? '取得できませんでした' : '確認しています…' }}</span>
                （現時点・有効な端末のみ）
              </p>
            </div>
            <div class="p-message-edit-list__item">
              <div class="c-input u-w50p">
                <div class="c-input__title">
                  <span class="c-input__text c-input__text--required">【必須】</span>管理用タイトル
                </div>
                <input
                  v-model="messageName"
                  class="c-input__item"
                  type="text"
                  name="message_name"
                  maxlength="300"
                  placeholder="例：9/30 セール告知"
                />
                <div class="c-error c-error--balloon">
                  <div class="c-error__text"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="l-main-content l-main-content--half l-main-content--left">
        <message-form
          ref="messageForm"
          v-model="message"
          :custom-utm-parameters="customUtmParameters"
          @optimize-start="isOptimizing = true"
          @optimize-end="isOptimizing = false"
        />
        <p class="unique-key-message-note">ユニークキー配信の本文は 512 文字までです。</p>
        <message-action-form ref="messageActionForm" v-model="messageAction" :skip-regular="true" />
      </div>
      <div class="l-main-content l-main-content--half l-main-content--right">
        <div class="p-message-preview">
          <div class="c-title c-title--left u-text-white-1">
            <div class="c-title__text u-fs--l">プレビュー</div>
            <div class="c-text u-fs10">実際の配信メッセージと表示が異なることがあります</div>
          </div>
          <ul class="c-tab-list">
            <li class="c-tab-list__item" :class="{ 'is-active': previewType == 'desktop' }" @click="previewType = 'desktop'">
              PCでの配信例
            </li>
            <li class="c-tab-list__item" :class="{ 'is-active': previewType == 'mobile' }" @click="previewType = 'mobile'">
              Androidでの配信例
            </li>
          </ul>
          <div v-show="previewType == 'desktop'" class="p-message-preview-list">
            <message-preview
              :title="message.messageTitle"
              :text="message.messageText"
              :url="message.clickUrl"
              :icon-url="previewIconUrl"
              :large-image-url="message.largeImageUrl"
              :has-button-a="message.hasButtonA"
              :has-button-b="message.hasButtonB"
              :action-button-text-a="message.actionButtonTextA"
              :action-button-url-a="message.actionButtonUrlA"
              :action-button-text-b="message.actionButtonTextB"
              :action-button-url-b="message.actionButtonUrlB"
            />
          </div>
          <div v-show="previewType == 'mobile'" class="p-message-preview-list">
            <message-preview-sp
              :title="message.messageTitle"
              :text="message.messageText"
              :url="message.clickUrl"
              :icon-url="previewIconUrl"
              :large-image-url="message.largeImageUrl"
              :badge-url="previewBadgeUrl"
              :has-button-a="message.hasButtonA"
              :has-button-b="message.hasButtonB"
              :action-button-text-a="message.actionButtonTextA"
              :action-button-url-a="message.actionButtonUrlA"
              :action-button-text-b="message.actionButtonTextB"
              :action-button-url-b="message.actionButtonUrlB"
            />
          </div>
        </div>
      </div>

      <div class="unique-key-message-actions">
        <a class="c-btn c-btn--gray u-w200 u-h50" :href="cancelUrl">
          <p class="c-btn__text">キャンセル</p>
        </a>
        <button
          type="button"
          class="c-btn c-btn--blue u-w320 u-h50 u-fs20"
          :class="{ 'is-loading': isSaving || isOptimizing }"
          :disabled="isSaving || isOptimizing"
          @click="onConfirmClick"
        >
          <p class="c-btn__text">確認画面へ</p>
        </button>
      </div>
    </main>

    <error-modal :is-active="errorMessage !== ''" :message="errorMessage" :on-close-click="onCloseErrorModal" />
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import dayjs from 'dayjs';

// cmsapi /unique_key/send と同じ上限
const MAX_TEXT_LENGTH = 512;
const MAX_PAYLOAD_BYTES = 4072 - 600;

export default {
  props: {
    csrfToken: String,
    messageInfo: Object,
    customUtmParameters: {
      type: [Object, Array],
      default: () => [],
    },
  },
  data() {
    return {
      lists: [],
      listId: this.messageInfo.listId,
      listError: '',
      reach: null,
      reachFailed: false,
      messageName: null,
      message: {
        messageTitle: null,
        messageText: null,
        clickUrl: null,
        iconUrl: null,
        selectedIconFile: null,
        largeImageUrl: null,
        selectedLargeImageFile: null,
        hasButtonA: false,
        hasButtonB: false,
        actionButtonTextA: null,
        actionButtonUrlA: null,
        actionButtonTextB: null,
        actionButtonUrlB: null,
        adFlag: null,
        badgeUrl: null,
        selectedBadgeFile: null,
        customUtmParameter: null,
        customUtmParameterActionA: null,
        customUtmParameterActionB: null,
      },
      messageAction: {
        requireInteraction: '0',
        sendStartDate: null,
        sendStartTime: null,
        deliverType: '1',
        regularType: [],
        regularStartDate: null,
        regularEndDate: null,
        targetStartTime: null,
      },
      previewType: 'desktop',
      isSaving: false,
      isOptimizing: false,
      errorMessage: '',
    };
  },
  computed: {
    previewIconUrl: function () {
      return this.message.iconUrl || this.messageInfo.defaultIconUrl;
    },
    previewBadgeUrl: function () {
      return this.message.badgeUrl || this.messageInfo.defaultBadgeUrl;
    },
    cancelUrl: function () {
      return this.listId
        ? '/coins-admin/unique-key-list/review?list_id=' + this.listId
        : '/coins-admin/unique-key-list';
    },
  },
  watch: {
    listId: function () {
      this.listError = '';
      this.loadReachCount();
    },
  },
  mounted: function () {
    import('../../funcs/pages/message-add').then((module) => {
      module.MessageAdd();
    });
    import('../../funcs/components/select').then((module) => {
      module.Select();
    });
    this.loadLists();
    if (this.messageInfo.messageId) {
      this.loadMessage();
    } else if (this.listId) {
      this.loadReachCount();
    }
  },
  methods: {
    loadLists: function () {
      axios
        .get('/api/unique-key-list/list', { params: { all: 1 } })
        .then((res) => {
          this.lists = res.data.lists;
        })
        .catch((error) => {
          this.handleHttpError(error);
          this.errorMessage = 'ユニークキーリストの取得に失敗しました。再度お試しください。';
        });
    },
    loadReachCount: function () {
      this.reach = null;
      this.reachFailed = false;
      if (!this.listId) {
        return;
      }
      const listId = this.listId;
      axios
        .get('/api/unique-key-list/reach-count', { params: { list_id: listId }, timeout: 65000 })
        .then((res) => {
          // 取得中にリストを選び直した場合は古い結果を捨てる
          if (listId === this.listId) {
            this.reach = res.data;
          }
        })
        .catch((error) => {
          this.handleHttpError(error);
          if (listId === this.listId) {
            this.reachFailed = true;
          }
        });
    },
    loadMessage: function () {
      axios
        .get('/api/unique-key-message/get', { params: { message_id: this.messageInfo.messageId } })
        .then((res) => {
          this.applyMessage(res.data.message);
          if (res.data.list && res.data.list.delete_flag == 0) {
            this.listId = res.data.list.list_id;
          }
        })
        .catch((error) => {
          this.handleHttpError(error);
          this.errorMessage = '配信内容の取得に失敗しました。再度お試しください。';
        });
    },
    applyMessage: function (m) {
      this.messageName = m.message_name;
      this.message.messageTitle = m.message_title;
      this.message.messageText = m.message_text;
      this.message.clickUrl = m.click_url;
      this.message.iconUrl = m.icon_url;
      this.message.largeImageUrl = m.large_image_url;
      this.message.badgeUrl = m.badge_image_url;
      this.message.hasButtonA = m.use_action_button_a == 1;
      this.message.actionButtonTextA = m.action_button_text_a;
      this.message.actionButtonUrlA = m.action_button_url_a;
      this.message.hasButtonB = m.use_action_button_b == 1;
      this.message.actionButtonTextB = m.action_button_text_b;
      this.message.actionButtonUrlB = m.action_button_url_b;
      this.messageAction.requireInteraction = String(m.require_interaction);
      this.messageAction.deliverType = String(m.delivery_type);
      if (this.messageAction.deliverType == '1') {
        this.messageAction.sendStartDate = m.send_start_date;
        this.messageAction.sendStartTime = dayjs(m.send_start_date).format('HH:mm');
      }
    },
    payloadBytes: function () {
      const values = [
        this.message.messageTitle,
        this.message.messageText,
        this.message.clickUrl,
        this.message.hasButtonA ? this.message.actionButtonTextA : '',
        this.message.hasButtonA ? this.message.actionButtonUrlA : '',
        // buildParams と同じく、ボタン2はボタン1があるときだけ送る
        this.message.hasButtonA && this.message.hasButtonB ? this.message.actionButtonTextB : '',
        this.message.hasButtonA && this.message.hasButtonB ? this.message.actionButtonUrlB : '',
      ];
      return new Blob([values.map((value) => value || '').join('')]).size;
    },
    validate: function () {
      const errors = {};
      this.listError = this.listId ? '' : '配信先のリストを選んでください';
      if (!this.messageName) {
        errors['message_name'] = '管理用タイトルを入力してください';
      }
      this.$refs.messageForm.validate(errors);
      this.$refs.messageActionForm.validate(errors);
      if (!errors['message_content'] && Array.from(this.message.messageText || '').length > MAX_TEXT_LENGTH) {
        errors['message_content'] = `ユニークキー配信の本文は${MAX_TEXT_LENGTH}文字以内で入力してください`;
      }
      if (!errors['message_content'] && this.payloadBytes() > MAX_PAYLOAD_BYTES) {
        errors['message_content'] = 'タイトル・本文・URL の合計が長すぎます。短くしてください';
      }
      this.showErrors(errors);

      return Object.keys(errors).length === 0 && this.listError === '';
    },
    appendForm: function (params, name, value) {
      if (value !== null && value !== undefined) {
        params.append(name, value);
      }
    },
    appendUtmParameter: function (url, value) {
      let parameterValue = this.customUtmParameters[value];
      if (!parameterValue || typeof parameterValue !== 'string') {
        return url;
      }
      if (parameterValue.startsWith('?') || parameterValue.startsWith('&')) {
        parameterValue = parameterValue.substring(1);
      }
      return url + (url.includes('?') ? '&' : '?') + parameterValue;
    },
    buildParams: function () {
      const m = this.message;
      const params = new FormData();
      this.appendForm(params, 'message_id', this.messageInfo.messageId);
      this.appendForm(params, 'list_id', this.listId);
      this.appendForm(params, 'message_name', this.messageName);
      this.appendForm(params, 'message_title', m.messageTitle);
      this.appendForm(params, 'message_text', m.messageText);
      this.appendForm(
        params,
        'click_url',
        m.customUtmParameter ? this.appendUtmParameter(m.clickUrl, m.customUtmParameter) : m.clickUrl
      );
      this.appendForm(params, 'image_url', m.iconUrl);
      this.appendForm(params, 'large_image_url', m.largeImageUrl);
      this.appendForm(params, 'badge_url', m.badgeUrl);
      this.appendForm(params, 'icon_file', m.selectedIconFile);
      this.appendForm(params, 'large_image_file', m.selectedLargeImageFile);
      this.appendForm(params, 'badge_file', m.selectedBadgeFile);
      this.appendForm(params, 'require_interaction', this.messageAction.requireInteraction);
      if (m.hasButtonA) {
        this.appendForm(params, 'action_button_text_a', m.actionButtonTextA);
        this.appendForm(
          params,
          'action_button_url_a',
          m.customUtmParameterActionA
            ? this.appendUtmParameter(m.actionButtonUrlA, m.customUtmParameterActionA)
            : m.actionButtonUrlA
        );
        if (m.hasButtonB) {
          this.appendForm(params, 'action_button_text_b', m.actionButtonTextB);
          this.appendForm(
            params,
            'action_button_url_b',
            m.customUtmParameterActionB
              ? this.appendUtmParameter(m.actionButtonUrlB, m.customUtmParameterActionB)
              : m.actionButtonUrlB
          );
        }
      }
      this.appendForm(params, 'deliver_type', this.messageAction.deliverType);
      if (this.messageAction.deliverType == '1') {
        params.append(
          'send_start_date',
          moment(this.messageAction.sendStartDate).format('YYYY-MM-DD') + ' ' + this.messageAction.sendStartTime + ':00'
        );
      }
      return params;
    },
    onConfirmClick: function () {
      if (this.isSaving || !this.validate()) {
        return;
      }
      this.isSaving = true;
      axios
        .post('/api/unique-key-message/save', this.buildParams())
        .then((res) => {
          if (res.data.result === 'OK') {
            location.href = '/coins-admin/unique-key-message/confirm?message_id=' + res.data.message_id;
            return;
          }
          this.isSaving = false;
          const errors = Object.assign({}, res.data.errors || {});
          if (errors.list_id) {
            this.listError = errors.list_id;
            delete errors.list_id;
          }
          if (Object.keys(errors).length > 0) {
            this.showErrors(errors);
          } else if (!this.listError) {
            this.errorMessage = res.data.message;
          }
        })
        .catch((error) => {
          this.handleHttpError(error);
          this.isSaving = false;
          this.errorMessage = '配信内容の保存に失敗しました。再度お試しください。';
        });
    },
    onCloseErrorModal: function () {
      this.errorMessage = '';
    },
  },
};
</script>

<style scoped>
/* 標準の矢印は枠に詰まって見えるので消し、共通の c-select と同じ三角を右に余白を取って置く */
.unique-key-message-list {
  width: 100%;
  height: 44px;
  padding: 0 40px 0 12px;
  border: 1px solid #d5dde4;
  border-radius: 4px;
  background: #fff
    url('data:image/svg+xml;charset=utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20384%20320%22%3E%3Cpath%20fill%3D%22%23999999%22%20d%3D%22M384%2C0L192%2C320L0%2C0L384%2C0z%22%2F%3E%3C%2Fsvg%3E')
    no-repeat right 15px center / 12.5px auto;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
}
.unique-key-message-reach {
  margin-top: 8px;
  font-size: 14px;
}
.unique-key-message-reach__value {
  font-weight: bold;
  font-variant-numeric: tabular-nums;
}
.unique-key-message-note {
  color: #666;
  font-size: 13px;
  margin: 8px 0 24px;
}
.unique-key-message-actions {
  display: flex;
  justify-content: center;
  gap: 24px;
  width: 100%;
  padding: 32px 0 64px;
}
</style>
